/**
 * Main layout component - Styled according to style.md
 */
import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  CSSObject,
  Theme,
  styled,
  useMediaQuery,
  Fade,
  Breadcrumbs,
  Link,
  Badge,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ChevronRight,
  ChevronLeft,
  Home,
  LayoutDashboard,
  Users,
  ClipboardList,
  GraduationCap,
  BarChart3,
  Settings,
  User,
  LogOut,
  Moon,
  Sun,
  Monitor,
  Briefcase,
  UserSearch,
  GitBranch,
  Building2,
  Clock,
  Bell,
  Mail,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useThemeMode } from '../../context/ThemeContext';
import GradientBackground from '../common/GradientBackground';
import IdleTimeoutHandler from '../common/IdleTimeoutHandler';
import NotificationDropdown from '../notifications/NotificationDropdown';
import { useNotifications } from '../../hooks/useNotifications';
import { Notification } from '../../types/notification';

// System type for toggle
type SystemType = 'ems' | 'ats';

// --- Configuration from your original file ---
const drawerWidth = 200;
const collapsedWidth = 70; // Width when closed based on your icon size
// Smooth transition with Material Design easing - includes margin-left for main content
const waterFlowTransition = 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1), top 0.4s cubic-bezier(0.4, 0, 0.2, 1), height 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)';

// --- 1. Define Mixins (The States) ---

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  top: '80px',
  height: 'calc(100vh - 100px)',
  borderRadius: '16px',
  transform: 'translateY(0)',
  transition: waterFlowTransition,
  overflowX: 'hidden',

  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(251, 146, 60, 0.1)' // Orange tint for dark mode
    : '1px solid rgba(249, 115, 22, 0.08)',

  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.9) 100%)' // Navy/Slate gradient
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(249, 250, 251, 0.85) 100%)',

  backdropFilter: 'blur(25px) saturate(180%)',
  WebkitBackdropFilter: 'blur(25px) saturate(180%)',

  boxShadow: theme.palette.mode === 'dark'
    ? '0px 8px 24px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
    : '0px 4px 20px rgba(249, 115, 22, 0.08), 0px 2px 8px rgba(0, 0, 0, 0.04)',
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: collapsedWidth,
  top: '50%',
  height: 'auto',
  paddingBottom: '8px',
  paddingTop: '8px',
  borderRadius: '14px',
  transform: 'translateY(-50%)',
  transition: waterFlowTransition,
  overflowX: 'hidden',

  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(251, 146, 60, 0.1)' // Orange tint for dark mode
    : '1px solid rgba(249, 115, 22, 0.08)',

  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.9) 100%)' // Navy/Slate gradient
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(249, 250, 251, 0.85) 100%)',

  backdropFilter: 'blur(25px) saturate(180%)',
  WebkitBackdropFilter: 'blur(25px) saturate(180%)',

  boxShadow: theme.palette.mode === 'dark'
    ? '0px 8px 24px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
    : '0px 4px 20px rgba(249, 115, 22, 0.08), 0px 2px 8px rgba(0, 0, 0, 0.04)',
});

// --- 2. Styled Components ---

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    // We apply the mixins to the PAPER element (the actual visible box)
    '& .MuiDrawer-paper': {
      position: 'fixed',
      left: 0,
      marginLeft: theme.spacing(1.5), // Original mx: 1.5
      zIndex: 10,
      ...(open && openedMixin(theme)),
      ...(!open && closedMixin(theme)),
    },
  }),
);

// This handles the MAIN content transition
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginTop: '60px', // AppBar height
  minHeight: 'calc(100vh - 60px)',
  overflow: 'hidden',
  // Critical: This margin transition MUST match the Drawer transition exactly
  transition: waterFlowTransition,
  // When open: account for drawerWidth + drawer's marginLeft (12px) + gap (4px)
  // When closed: account for collapsedWidth + drawer's marginLeft (12px) + gap (4px)
  marginLeft: open ? `${drawerWidth -200}px` : `${collapsedWidth -200}px`,
  width: `calc(100% - ${open ? drawerWidth -200  : collapsedWidth -200 }px)`,
}));

const Layout: React.FC = () => {
  const isSmallScreen = useMediaQuery('(max-width:768px)');
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastNotification, setToastNotification] = useState<Notification | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasRole } = useAuth();
  const { themeMode, setThemeMode, effectiveTheme } = useThemeMode();

  // Use notifications hook with adaptive polling and toast notifications
  const {
    unreadCount,
  } = useNotifications({
    enablePolling: true,
    limit: 10,
    onNewNotification: (notification) => {
      // Show toast notification for new items
      setToastNotification(notification);
      setToastOpen(true);
    }
  });

  // Handle toast close
  const handleToastClose = () => {
    setToastOpen(false);
  };

  // Determine current system based on URL path (primary source of truth)
  const getSystemFromPath = (pathname: string): SystemType => {
    return pathname.startsWith('/ats') ? 'ats' : 'ems';
  };

  const [currentSystem, setCurrentSystem] = useState<SystemType>(() => {
    // Initialize from URL path, not localStorage
    return getSystemFromPath(window.location.pathname);
  });

  // State for fade transition
  const [fadeIn, setFadeIn] = useState(true);
  const [prevPath, setPrevPath] = useState(location.pathname);

  // Sync toggle state with URL path changes (e.g., browser back/forward)
  useEffect(() => {
    const systemFromPath = getSystemFromPath(location.pathname);
    if (currentSystem !== systemFromPath) {
      setCurrentSystem(systemFromPath);
    }
  }, [location.pathname, currentSystem]);

  // Handle route transitions with fade effect
  useEffect(() => {
    if (location.pathname !== prevPath) {
      // Trigger fade out then fade in
      setFadeIn(false);
      const timer = setTimeout(() => {
        setPrevPath(location.pathname);
        setFadeIn(true);
      }, 250); // Match exit timeout
      return () => clearTimeout(timer);
    }
  }, [location.pathname, prevPath]);

  // Sync current system to localStorage for persistence
  useEffect(() => {
    localStorage.setItem('currentSystem', currentSystem);
  }, [currentSystem]);

  // Handle system toggle change
  const handleSystemChange = (
    _event: React.MouseEvent<HTMLElement>,
    newSystem: SystemType | null
  ) => {
    if (newSystem !== null) {
      setCurrentSystem(newSystem);
      // Navigate to respective dashboard
      if (newSystem === 'ats') {
        navigate('/ats');
      } else {
        navigate('/dashboard');
      }
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => setProfileAnchor(event.currentTarget);
  const handleProfileMenuClose = () => setProfileAnchor(null);

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const handleThemeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: 'light' | 'dark' | 'system' | null
  ) => {
    if (newMode !== null) {
      setThemeMode(newMode);
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };


  // EMS Menu Items
  const emsMenuItems = [
    { text: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR', 'OFFSHORE_IT', 'ONSHORE_IT'] },
    { text: 'Employees', icon: Users, path: '/employees', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR', 'OFFSHORE_IT', 'ONSHORE_IT'] },
    { text: 'Organization', icon: GitBranch, path: '/organization', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR', 'OFFSHORE_IT', 'ONSHORE_IT'] },
    { text: 'Clients', icon: Building2, path: '/clients', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR', 'OFFSHORE_IT', 'ONSHORE_IT'] },
    { text: 'Projects', icon: ClipboardList, path: '/projects', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR', 'OFFSHORE_IT', 'ONSHORE_IT'] },
    { text: 'Timesheets', icon: Clock, path: '/timesheets', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR'] },
    { text: 'Onboarding', icon: GraduationCap, path: '/onboarding', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR', 'OFFSHORE_IT', 'ONSHORE_IT'] },
    { text: 'Reports', icon: BarChart3, path: '/reports', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR'] },
    { text: 'Email Requests', icon: Mail, path: '/generic-email-requests', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR', 'OFFSHORE_IT', 'ONSHORE_IT'] },
    //{ text: 'Admin Settings', icon: Settings, path: '/users', roles: ['ADMIN'] },
    // { text: 'Allocations', icon: BarChart3, path: '/allocation-reports', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR'] }, // Temporarily hidden
  ];

  // ATS Menu Items
  const atsMenuItems = [
    { text: 'Dashboard', icon: LayoutDashboard, path: '/ats', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR'] },
    { text: 'Open Roles', icon: Briefcase, path: '/ats/open-roles', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR'] },
    { text: 'Candidates', icon: UserSearch, path: '/ats/candidates', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR'] },
    { text: 'Recent Hires', icon: Users, path: '/ats/recent-hires', roles: ['ADMIN', 'ONSHORE_HR', 'OFFSHORE_HR'] },
  ];

  // Select menu items based on current system
  const menuItems = currentSystem === 'ats' ? atsMenuItems : emsMenuItems;

  // Generate breadcrumbs based on current path
  const getBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter(x => x);

    // Path label mapping
    const pathLabels: Record<string, string> = {
      'dashboard': 'Dashboard',
      'employees': 'Employees',
      'projects': 'Projects',
      'clients': 'Clients',
      'organization': 'Organization Hierarchy',
      'onboarding': 'Onboarding',
      'reports': 'Reports',
      'allocation-reports': 'Allocation Reports',
      'users': 'Admin Settings',
      'profile': 'Profile',
      'ats': 'ATS Dashboard',
      'open-roles': 'Open Roles',
      'candidates': 'Candidates',
      'recent-hires': 'Recent Hires',
    };

    // Build breadcrumb items
    const breadcrumbItems: { label: string; path: string; isLast: boolean }[] = [];

    pathnames.forEach((segment, index) => {
      // Skip the root-level segments so they don't repeat after "Home"
      if (segment === 'dashboard' || segment === 'ats' || segment === 'home') {
        return;
      }

      // Skip numeric segments (IDs) - e.g., "1020", "1038", "5"
      if (/^\d+$/.test(segment)) {
        return;
      }

      // Skip "pipeline" and "applications" segments
      if (segment === 'pipeline' || segment === 'applications') {
        return;
      }

      const path = `/${pathnames.slice(0, index + 1).join('/')}`;
      const label = pathLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
      const isLast = index === pathnames.length - 1;

      breadcrumbItems.push({ label, path, isLast });
    });

    return breadcrumbItems;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <GradientBackground />

      {/* App Bar - Enhanced Liquid Glass Effect */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          height: 60,
          // Liquid glass background with gradient
          background: effectiveTheme === 'dark'
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.85) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(249, 250, 251, 0.85) 100%)',
          backdropFilter: 'saturate(200%) blur(25px)',
          WebkitBackdropFilter: 'saturate(200%) blur(25px)',
          borderBottom: effectiveTheme === 'dark'
            ? '1px solid rgba(251, 146, 60, 0.1)'
            : '1px solid rgba(249, 115, 22, 0.08)',
          boxShadow: effectiveTheme === 'dark'
            ? 'inset 0 -1px 0 rgba(251, 146, 60, 0.08)'
            : 'inset 0 -1px 0 rgba(249, 115, 22, 0.1)',
        }}
      >
        <Toolbar sx={{ height: 60, px: 2.5, minHeight: '60px !important' }}>
          <Box
            component="img"
            src={isSmallScreen ? 'DatafactZ logo.png' : (effectiveTheme === 'light' ? 'Datafact Z New Logo Dark.png' : 'Datafact Z New Logo.png')}
            alt="DataFactz"
            sx={{
              height: '1.75rem',
              mr: 3,
              ml: 2,
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
              '&:hover': { opacity: 0.8 },
            }}
            onClick={() => navigate(currentSystem === 'ems' ? '/dashboard' : '/ats')}
          />

          {/* Divider */}
          <Box
            sx={{
              width: '1px',
              height: '24px',
              bgcolor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
              mr: 3,
            }}
          />

          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              color: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : '#1D1D1F',
              flexGrow: 1,
              fontWeight: 500,
              fontSize: '1rem',
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              letterSpacing: '-0.01em',
            }}
          >
            {currentSystem === 'ems'
              ? (isSmallScreen ? 'EMS' : 'Employee Management System') : (isSmallScreen ? 'ATS' : 'Application Tracking System')
            }
          </Typography>

          {isSmallScreen && <Box sx={{ flexGrow: 1 }} />}

          {/* EMS/ATS System Toggle */}
          <ToggleButtonGroup
            value={currentSystem}
            exclusive
            onChange={handleSystemChange}
            size="small"
            sx={{
              mr: 1.5,
              bgcolor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
              borderRadius: '8px',
              padding: '3px',
              position: 'relative',
              overflow: 'visible',

              // Sliding background indicator
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '3px',
                left: currentSystem === 'ems' ? '3px' : 'calc(50% + 0px)',
                width: 'calc(50% - 3px)',
                height: 'calc(100% - 6px)',
                borderRadius: '6px',

                // Pale orange background
                bgcolor: effectiveTheme === 'dark'
                  ? 'rgba(251, 146, 60, 0.18)'
                  : 'rgba(249, 115, 22, 0.12)',

                // Subtle shadow for depth
                boxShadow: effectiveTheme === 'dark'
                  ? '0 2px 8px rgba(251, 146, 60, 0.2), inset 0 1px 0 rgba(251, 146, 60, 0.08)'
                  : '0 2px 6px rgba(249, 115, 22, 0.15), inset 0 1px 0 rgba(249, 115, 22, 0.1)',

                // Border for extra definition
                border: effectiveTheme === 'dark'
                  ? '1px solid rgba(251, 146, 60, 0.25)'
                  : '1px solid rgba(249, 115, 22, 0.2)',

                // Smooth sliding transition
                transition: 'left 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s ease',
                zIndex: 0,
              },

              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '6px !important',
                px: 1.5,
                py: 0.4,
                fontSize: '0.75rem',
                fontWeight: 600,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                textTransform: 'none',
                color: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.45)',
                zIndex: 1,
                position: 'relative',
                bgcolor: 'transparent',

                // Enhanced smooth text color transition
                transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s ease',

                '&.Mui-selected': {
                  bgcolor: 'transparent',

                  // Enhanced text color for contrast
                  color: effectiveTheme === 'dark'
                    ? '#FB923C'  // Light orange (softer for dark mode)
                    : '#EA580C', // Dark orange (stronger for light mode)

                  // Hover state for selected button
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: effectiveTheme === 'dark'
                      ? '#FCA967'  // Slightly lighter on hover
                      : '#DC2626', // Slightly darker on hover
                  },
                },
                '&:hover': {
                  bgcolor: 'transparent',
                  transform: 'scale(1.02)',
                },
              },
            }}
          >
            <ToggleButton value="ems">EMS</ToggleButton>
            <ToggleButton value="ats">ATS</ToggleButton>
          </ToggleButtonGroup>

          {/* Notifications Bell with Badge */}
          <IconButton
            onClick={(e) => setNotificationAnchor(e.currentTarget)}
            sx={{
              mr: 1,
              color: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
              width: 36,
              height: 36,
              '&:hover': {
                bgcolor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Badge badgeContent={unreadCount || 0} color="error">
              <Bell size={18} strokeWidth={1.75} />
            </Badge>
          </IconButton>
          <NotificationDropdown
            open={Boolean(notificationAnchor)}
            anchorEl={notificationAnchor}
            onClose={() => setNotificationAnchor(null)}
          />

          {/* Theme Toggle */}
          <ToggleButtonGroup
            value={themeMode}
            exclusive
            onChange={handleThemeChange}
            size="small"
            sx={{
              mr: 1.5,
              bgcolor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
              borderRadius: '8px',
              padding: '3px',
              position: 'relative',
              overflow: 'visible',

              // Sliding background indicator
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '3px',
                left: themeMode === 'light' ? '3px' : themeMode === 'dark' ? 'calc(33.333% + 1px)' : 'calc(66.666% - 1px)',
                width: 'calc(33.333% - 2px)',
                height: 'calc(100% - 6px)',
                borderRadius: '6px',

                // Pale orange background
                bgcolor: effectiveTheme === 'dark'
                  ? 'rgba(251, 146, 60, 0.18)'
                  : 'rgba(249, 115, 22, 0.12)',

                // Subtle shadow for depth
                boxShadow: effectiveTheme === 'dark'
                  ? '0 2px 8px rgba(251, 146, 60, 0.2), inset 0 1px 0 rgba(251, 146, 60, 0.08)'
                  : '0 2px 6px rgba(249, 115, 22, 0.15), inset 0 1px 0 rgba(249, 115, 22, 0.1)',

                // Border for extra definition
                border: effectiveTheme === 'dark'
                  ? '1px solid rgba(251, 146, 60, 0.25)'
                  : '1px solid rgba(249, 115, 22, 0.2)',

                // Smooth sliding transition
                transition: 'left 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s ease',
                zIndex: 0,
              },

              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '6px !important',
                px: 0.75,
                py: 0.4,
                minWidth: '32px',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.45)',
                zIndex: 1,
                position: 'relative',
                bgcolor: 'transparent',

                // Enhanced smooth text color transition
                transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s ease',

                '&.Mui-selected': {
                  bgcolor: 'transparent',

                  // Enhanced text color for contrast
                  color: effectiveTheme === 'dark'
                    ? '#FB923C'  // Light orange (softer for dark mode)
                    : '#EA580C', // Dark orange (stronger for light mode)

                  // Hover state for selected button
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: effectiveTheme === 'dark'
                      ? '#FCA967'  // Slightly lighter on hover
                      : '#DC2626', // Slightly darker on hover
                  },
                },
                '&:hover': {
                  bgcolor: 'transparent',
                  transform: 'scale(1.05)',
                },
              },
            }}
          >
            <ToggleButton value="light">
              <Sun size={16} strokeWidth={1.75} />
            </ToggleButton>
            <ToggleButton value="dark">
              <Moon size={16} strokeWidth={1.75} />
            </ToggleButton>
            <ToggleButton value="system">
              <Monitor size={16} strokeWidth={1.75} />
            </ToggleButton>
          </ToggleButtonGroup>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
            onClick={handleProfileMenuOpen}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,

                // Glassmorphism background
                background: effectiveTheme === 'dark'
                  ? 'linear-gradient(135deg, rgba(251, 146, 60, 0.25) 0%, rgba(249, 115, 22, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(249, 115, 22, 0.2) 0%, rgba(251, 146, 60, 0.15) 100%)',

                backdropFilter: 'blur(12px) saturate(180%)',
                WebkitBackdropFilter: 'blur(12px) saturate(180%)',

                // Border with orange glow
                border: effectiveTheme === 'dark'
                  ? '1.5px solid rgba(251, 146, 60, 0.4)'
                  : '1.5px solid rgba(249, 115, 22, 0.3)',

                // Glass shadow
                boxShadow: effectiveTheme === 'dark'
                  ? '0 4px 12px rgba(251, 146, 60, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                  : '0 4px 12px rgba(249, 115, 22, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.4)',

                fontWeight: 600,
                fontSize: '0.875rem',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',

                // Enhanced text color for contrast
                color: effectiveTheme === 'dark'
                  ? '#FB923C'  // Light orange for dark mode
                  : '#EA580C', // Dark orange for light mode

                transition: 'all 0.3s ease',
              }}
            >
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer variant="permanent" open={drawerOpen && !isSmallScreen}>

        {/* Toggle Button Inside Drawer */}
        {!isSmallScreen && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: drawerOpen ? 'flex-end' : 'center', py: 1.5, px: drawerOpen ? 1.5 : 0, width: '100%' }}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                bgcolor: effectiveTheme === 'dark'
                  ? 'rgba(255, 255, 255, 0.04)'
                  : 'rgba(0, 0, 0, 0.03)',
                color: effectiveTheme === 'dark'
                  ? 'rgba(255, 255, 255, 0.5)'
                  : '#9CA3AF',
                width: 32,
                height: 32,
                borderRadius: '8px',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: effectiveTheme === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.06)',
                  color: effectiveTheme === 'dark'
                    ? 'rgba(255, 255, 255, 0.8)'
                    : '#6B7280',
                }
              }}
            >
              {drawerOpen ? <ChevronLeft size={16} strokeWidth={1.75} /> : <ChevronRight size={16} strokeWidth={1.75} />}
            </IconButton>
          </Box>
        )}

        <Fade in={true} timeout={300} key={currentSystem}>
          <List sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, width: '100%', px: 0.75, pt: 0.5 }}>
            {menuItems
              .filter((item) => hasRole(item.roles))
              .map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                const isOpen = drawerOpen && !isSmallScreen;

              return (
                <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.25 }}>
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    sx={{
                      minHeight: isOpen ? 44 : 44,
                      width: isOpen ? '90%' : '44px',
                      height: isOpen ? 'auto' : '44px',
                      mx: 'auto',
                      justifyContent: isOpen ? 'flex-start' : 'center',
                      alignItems: 'center',
                      borderRadius: '10px',
                      background: isActive
                        ? (effectiveTheme === 'dark'
                            ? 'rgba(249, 115, 22, 0.15)'
                            : 'rgba(249, 115, 22, 0.1)')
                        : 'transparent',
                      color: isActive
                        ? '#F97316'
                        : (effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#6B7280'),
                      boxShadow: 'none',
                      transition: 'all 0.15s ease',
                      px: isOpen ? 2 : 0,
                      py: isOpen ? 0.75 : 0,
                      '&:hover': {
                        background: isActive
                          ? (effectiveTheme === 'dark'
                              ? 'rgba(249, 115, 22, 0.2)'
                              : 'rgba(249, 115, 22, 0.12)')
                          : (effectiveTheme === 'dark'
                              ? 'rgba(255, 255, 255, 0.06)'
                              : 'rgba(0, 0, 0, 0.04)'),
                        color: isActive
                          ? '#F97316'
                          : (effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#1D1D1F'),
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        minWidth: 0,
                        mr: isOpen ? 1.5 : 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon size={isOpen ? 18 : 20} strokeWidth={1.75} />
                    </Box>
                    <Typography
                      sx={{
                        opacity: isOpen ? 1 : 0,
                        width: isOpen ? 'auto' : 0,
                        fontWeight: isActive ? 600 : 500,
                        fontSize: '0.875rem',
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                        letterSpacing: '-0.01em',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        transition: 'opacity 0.15s ease',
                      }}
                    >
                      {item.text}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              );
              })}
          </List>
        </Fade>
      </Drawer>

      {/* Main Content using the Synchronized styled component */}
      <Main open={drawerOpen && !isSmallScreen}>
        {/* Breadcrumb Navigation */}
        {breadcrumbs.length > 0 && (
          <Breadcrumbs
            separator={<ChevronRight size={16} strokeWidth={1.75} />}
            aria-label="breadcrumb"
            sx={{
              mb: 2,
              '& .MuiBreadcrumbs-ol': {
                flexWrap: 'nowrap',
              },
              '& .MuiBreadcrumbs-separator': {
                mx: 1,
                color: 'text.secondary',
              },
            }}
          >
            <Link
              underline="hover"
              color="text.secondary"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate(currentSystem === 'ats' ? '/ats' : '/dashboard');
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.875rem',
                gap: 0.5,
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <Home size={16} strokeWidth={1.75} />
              Home
            </Link>
            {breadcrumbs.map((crumb) => (
              crumb.isLast ? (
                <Typography
                  key={crumb.path}
                  color="text.primary"
                  sx={{ fontSize: '0.875rem', fontWeight: 600 }}
                >
                  {crumb.label}
                </Typography>
              ) : (
                <Link
                  key={crumb.path}
                  underline="hover"
                  color="text.secondary"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(crumb.path);
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {crumb.label}
                </Link>
              )
            ))}
          </Breadcrumbs>
        )}
        {/* Fade transition for dashboard content */}
        <Fade
          in={fadeIn}
          timeout={{ enter: 400, exit: 250 }}
        >
          <Box>
            <Outlet />
          </Box>
        </Fade>
      </Main>

      {/* Menus... */}
      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={handleProfileMenuClose}
        sx={{ mt: 1 }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }} sx={{ gap: 1.5 }}>
          <User size={18} strokeWidth={1.75} /> Profile
        </MenuItem>
        {hasRole(['ADMIN']) && (
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/users'); }} sx={{ gap: 1.5 }}>
            <Settings size={18} strokeWidth={1.75} /> Admin Settings
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ gap: 1.5 }}>
          <LogOut size={18} strokeWidth={1.75} /> Logout
        </MenuItem>
      </Menu>

      {/* Idle Timeout Handler - logs out after 30 minutes of inactivity */}
      <IdleTimeoutHandler timeout={30} warningTime={60} />

      {/* Toast notification for new notifications */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={handleToastClose}
          severity="info"
          variant="filled"
          sx={{
            width: '100%',
            bgcolor: effectiveTheme === 'dark' ? 'rgba(33, 150, 243, 0.95)' : 'primary.main',
            color: 'primary.contrastText'
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            {toastNotification?.title || 'New Notification'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {toastNotification?.message || ''}
          </Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Layout;